import { useNavigate } from '@tanstack/react-router';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import { Toggle } from 'components/Toggle/Toggle';
import useCountry from 'hooks/useCountry';
import { useOtpEnabled } from 'hooks/useOtpStatus';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { useGetProfileQuery } from 'store/slices/profile-api';
import appHelpers from 'utils/app-urls';
import verification from '../../../../../data/verification';
import messages from './messages';

import notificationsIcon from '../../assets/icons/settings_notificationsIcon.svg';
import twoFactorIcon from '../../assets/icons/settings_twoFactorIcon.svg';
import otpIcon from '../../assets/icons/settings_otpIcon.svg';
import { getProfileVerificationCard } from 'utils/verification-states';
import clsx from 'clsx';

const cdnURL = appHelpers.getCDNUrl();

export default function ProfileView({
  showModal,
  toggleLoginNotifications,
  closeModal,
}: {
  showModal?: boolean;
  toggleLoginNotifications?: (
    newValue: boolean,
    modalConfirmed: boolean,
  ) => void;
  closeModal?: () => void;
}) {
  const { data: userProfileResponse } = useGetProfileQuery();
  const { language } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();
  const countries = useCountry();
  const otpEnabled = useOtpEnabled();

  const { profileViewMessages } = messages[language];

  function getCountryName(countryCode: string | undefined): string {
    if (!countryCode) return '';
    const country = countries?.find(x => x.code === countryCode);

    if (country == null) return '';

    return country.name;
  }

  const twoFactorEnabled = userProfileResponse?.twoFactorStatus?.activated;
  const emailNotificationEnabled = userProfileResponse?.loginNotification;

  const verificationCard = getProfileVerificationCard(
    userProfileResponse?.verificationStatus ?? '',
    profileViewMessages,
  );
  function getModalContent() {
    return (
      <div>
        <div className="mb-8">
          <img
            src={`${cdnURL}assets/components/modal/exclamation.svg`}
            alt={profileViewMessages.disableNotificationsAlt}
            className="center"
          />
        </div>
        <p>{profileViewMessages.disableNotificationsModalBody}</p>
        <div className="mt-4">
          <Button
            onClick={() => {
              if (toggleLoginNotifications)
                toggleLoginNotifications(false, true);
            }}
            fluid
            small
            className="md:w-36 w-24  mr-2">
            {profileViewMessages.yesButton}
          </Button>
          <Button
            onClick={() => {
              if (closeModal) closeModal();
            }}
            fluid
            small
            className="md:w-36 w-24 ml-2"
            secondary>
            {profileViewMessages.noButton}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <h1 className="font-semibold text-2.5x1 text-left mb-2">
          {userProfileResponse?.firstName}&nbsp;
          {userProfileResponse?.lastName}&nbsp;
        </h1>

        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={verificationCard.icon}
                  alt={profileViewMessages.verifyAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              {userProfileResponse?.verificationStatus ===
              verification.Verified ? (
                <>
                  <div className={'text-header-text -mt-0.5'}>
                    {verificationCard.title}
                  </div>
                </>
              ) : (
                <>
                  <div className={'text-header-text -mt-0.5'}>
                    {verificationCard.title}
                  </div>
                  <div
                    className={clsx(
                      'text-sm',
                      verificationCard.subTextClassName,
                    )}>
                    {verificationCard.subTitle}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col h-12 justify-center ml-auto">
            {userProfileResponse?.verificationStatus ===
              verification.Unverified ||
            userProfileResponse?.verificationStatus ===
              verification.Incomplete ? (
              <Button
                secondary
                type="button"
                self-end
                className="xs:h-auto xs:px-3 p-2 w-auto"
                onClick={() => navigate({ to: '/verification' })}>
                {profileViewMessages.verifyButton}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={twoFactorIcon}
                  alt={profileViewMessages.twoFactorAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.twoFactorStatusLabel}
              </div>
              <div className="text-header-text -mt-0.5">
                {twoFactorEnabled
                  ? profileViewMessages.twoFactorEnabled
                  : profileViewMessages.twoFactorDisabled}
              </div>
            </div>
          </div>
          <div className="flex flex-col h-12 justify-center ml-auto">
            <Button
              secondary
              type="button"
              self-end
              onClick={() => navigate({ to: '/settings/two-factor' })}
              className="xs:h-auto xs:px-3 p-2 w-auto">
              {twoFactorEnabled
                ? profileViewMessages.disabledTwoFactorButton
                : profileViewMessages.enableTwoFactorButton}
            </Button>
          </div>
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={otpIcon}
                  alt={profileViewMessages.otpAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.otpStatusLabel}
              </div>
              <div className="text-header-text -mt-0.5">
                {otpEnabled
                  ? profileViewMessages.otpEnabled
                  : userProfileResponse?.verificationStatus?.toLowerCase() ===
                      'verified'
                    ? profileViewMessages.otpDisabled
                    : profileViewMessages.verifyToEnableOtp}
              </div>
            </div>
          </div>
          {!otpEnabled &&
            userProfileResponse?.verificationStatus?.toLowerCase() ===
              'verified' && (
              <div className="flex flex-col h-12 justify-center ml-auto">
                <Button
                  secondary
                  type="button"
                  self-end
                  onClick={() => navigate({ to: '/settings/otp/enable' })}
                  className="xs:h-auto xs:px-3 p-2 w-auto">
                  {profileViewMessages.enableOtpButton}
                </Button>
              </div>
            )}
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={notificationsIcon}
                  alt={profileViewMessages.emailNotificationAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.emailNotificationLabel}
              </div>
              <div className="text-header-text -mt-0.5">
                {emailNotificationEnabled
                  ? profileViewMessages.emailNotificationEnabled
                  : profileViewMessages.emailNotificationDisabled}
              </div>
            </div>
          </div>
          <div className="flex flex-col h-12 justify-center ml-auto">
            <Toggle
              self-end
              value={emailNotificationEnabled ?? false}
              onChange={(newValue: boolean) => {
                if (toggleLoginNotifications)
                  toggleLoginNotifications(newValue, false);
              }}
            />
          </div>
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="w-8 flex items-center justify-center overflow-hidden">
                <img
                  src={`${cdnURL}assets/flags/square/${userProfileResponse?.countryCode?.toLowerCase()}.svg`}
                  alt={userProfileResponse?.countryCode?.toLowerCase()}
                  className="w-full rounded-full"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.countryLabel}
              </div>
              <div className="text-header-text -mt-1">
                {getCountryName(userProfileResponse?.countryCode ?? undefined)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        hideModal={() => {
          if (closeModal) closeModal();
        }}
        show={showModal || false}
        content={getModalContent()}
        closeModal={() => {
          if (closeModal) closeModal();
        }}
      />
    </>
  );
}
